import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ProfileService } from '../../../../services/profile-service';
import { UserProfileDTO } from '../../../../services/profile-service.model';
import { finalize, switchMap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-update-profile-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './update-profile-form.html',
  styleUrl: './update-profile-form.scss',
})
export class UpdateProfileForm implements OnInit {
  private router = inject(Router);
  private profileService = inject(ProfileService);

  isAr = document.documentElement.dir === 'rtl';

  private get profileBasePath(): string {
  return this.router.url.includes('/admin') ? '/admin/profile' : '/user/profile';
}

  updateForm = new FormGroup({
    firstName: new FormControl('', [
      Validators.required, 
      Validators.minLength(2), 
      Validators.maxLength(100),
      Validators.pattern(/^[a-zA-Z\u0600-\u06FF\s]*$/)
    ]),
    lastName: new FormControl('', [
      Validators.required, 
      Validators.minLength(2), 
      Validators.maxLength(100),
      Validators.pattern(/^[a-zA-Z\u0600-\u06FF\s]*$/)
    ]),
    email: new FormControl('', [
      Validators.minLength(5),
      Validators.maxLength(150),
      Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
    ]),
    mobileNumber: new FormControl(''),
    gender: new FormControl<'M' | 'F'>('M'),
    birthDate: new FormControl(''),
    defaultLanguage: new FormControl('EN', [Validators.required]),
  });

  loading = signal(false);
  apiError = signal('');
  serverErrors: Record<string, string> = {};

  ngOnInit() {
    this.loadUserProfile();
  }

  loadUserProfile() {
    this.profileService.getUserProfile().subscribe({
      next: (profile) => {
        let mobile = profile.mobileNumber;
        if (mobile && mobile.startsWith('+20')) {
          mobile = mobile.substring(3);
        }
        this.updateForm.patchValue({
          firstName: profile.firstName,
          lastName: profile.lastName,
          email: profile.email,
          mobileNumber: mobile,
          gender: profile.gender,
          birthDate: profile.birthDate,
          defaultLanguage: profile.defaultLanguage,
        });
        this.updateForm.markAsPristine();
      }
    });
  }

  getErrorMessage(controlName: string): string {
    const control = this.updateForm.get(controlName);
    if (!control || !control.errors || !control.touched) return '';

    const errors = control.errors;

    if (controlName === 'firstName') {
      if (errors['required']) return this.isAr ? 'الاسم الأول مطلوب' : 'First Name is required';
      if (errors['minlength']) return this.isAr ? 'الاسم الأول يجب أن يكون حرفين على الأقل' : 'First Name must be at least 2 characters';
      if (errors['maxlength']) return this.isAr ? 'الاسم الأول لا يمكن أن يتجاوز 100 حرف' : 'First Name cannot exceed 100 characters';
      if (errors['pattern']) return this.isAr ? 'الاسم الأول يمكن أن يحتوي على حروف ومسافات فقط' : 'First Name can only contain letters and spaces';
    }

    if (controlName === 'lastName') {
      if (errors['required']) return this.isAr ? 'الاسم الأخير مطلوب' : 'Last Name is required';
      if (errors['minlength']) return this.isAr ? 'الاسم الأخير يجب أن يكون حرفين على الأقل' : 'Last Name must be at least 2 characters';
      if (errors['maxlength']) return this.isAr ? 'الاسم الأخير لا يمكن أن يتجاوز 100 حرف' : 'Last Name cannot exceed 100 characters';
      if (errors['pattern']) return this.isAr ? 'الاسم الأخير يمكن أن يحتوي على حروف ومسافات فقط' : 'Last Name can only contain letters and spaces';
    }

    if (controlName === 'email') {
      if (errors['pattern']) return this.isAr ? 'يرجى إدخال عنوان بريد إلكتروني صحيح' : 'Please enter a valid email address';
      if (errors['maxlength']) return this.isAr ? 'البريد الإلكتروني لا يمكن أن يتجاوز 150 حرف' : 'Email cannot exceed 150 characters';
    }

    return '';
  }

  onSave() {
    if (this.updateForm.invalid) {
      this.updateForm.markAllAsTouched();
      return;
    }
    this.apiError.set('');
    this.serverErrors = {};
    this.loading.set(true);

    const formValue = this.updateForm.value;
    const profileData: UserProfileDTO = {
      firstName: formValue.firstName?.trim()!,
      lastName: formValue.lastName?.trim()!,
      email: formValue.email?.trim() || null,
      mobileNumber: formValue.mobileNumber ? `+20${formValue.mobileNumber}` : '',
      gender: (formValue.gender as 'M' | 'F') || 'M',
      birthDate: formValue.birthDate || null,
    };

    this.profileService.updateUserProfile(profileData)
      .pipe(
        switchMap(() => this.profileService.updateUserDefaultLanguage({
          defaultLanguage: formValue.defaultLanguage!,
        })),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: () => this.router.navigate([this.profileBasePath]),
        error: (err: HttpErrorResponse) => {
          const msg = this.isAr ? err.error?.messageAr : err.error?.messageEn;
          this.apiError.set(msg || (this.isAr ? 'حدث خطأ غير متوقع' : 'An unexpected error occurred'));
        },
      });
  }

  onCancel() {
    this.router.navigate([this.profileBasePath]);
  }
}